import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Palette } from 'lucide-react';
import { ChatbotConfig } from '@/services/chatbotService';
import { useTranslation } from 'react-i18next';

interface AppearanceSettingsProps {
  config: ChatbotConfig;
  onUpdate: (updates: Partial<ChatbotConfig>) => void;
}

export function AppearanceSettings({ config, onUpdate }: AppearanceSettingsProps) {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  
  return (
    <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200">
      <CardHeader className="pb-4 border-b border-slate-100/80">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="w-8 h-8 rounded-lg bg-purple-100 border border-purple-200/50 flex items-center justify-center shadow-sm">
            <Palette className="w-4 h-4 text-purple-600" />
          </div>
          {t('appearanceBehavior')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-6">
        <div className="space-y-2">
          <Label htmlFor="primaryColor">{t('brandColor')}</Label>
          <div className="flex items-center gap-2">
            <Input
              type="color"
              value={config.primaryColor}
              onChange={e => onUpdate({ primaryColor: e.target.value })}
              className="w-12 h-10 p-1 cursor-pointer border-slate-200/80 shadow-sm"
            />
            <Input 
              value={config.primaryColor}
              onChange={e => onUpdate({ primaryColor: e.target.value })}
              className="font-mono text-sm border-slate-200/80 shadow-sm"
            />
          </div>
          <p className="text-xs text-slate-600">
            {t('chooseBrandColor')}
          </p>
        </div>

        <div className="space-y-4">
          <Label>{t('visitorInteraction')}</Label>
          <div className="space-y-4 p-4 rounded-lg bg-slate-50/50 border border-slate-200/60">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">{t('collectVisitorNames')}</Label>
                <p className="text-xs text-slate-600">{t('askForNameBeforeChat')}</p>
              </div>
              <Switch
                checked={config.collectUserInfo}
                onCheckedChange={checked => onUpdate({ collectUserInfo: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">{t('requireEmailAddress')}</Label>
                <p className="text-xs text-slate-600">{t('collectEmailForFollowUp')}</p>
              </div>
              <Switch
                checked={config.requireEmail}
                onCheckedChange={checked => onUpdate({ requireEmail: checked })}
                disabled={!config.collectUserInfo}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">{t('autoOpenChat')}</Label>
                <p className="text-xs text-slate-600">{t('greetVisitorsAutomatically')}</p>
              </div>
              <Switch
                checked={config.autoOpen}
                onCheckedChange={checked => onUpdate({ autoOpen: checked })}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 